import { Component, Injector, OnInit, AfterViewInit, HostListener } from '@angular/core';
import { Router, RoutesRecognized, ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';
import { Location } from '@angular/common';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Core, OvsComponent, ApplicationConfiguration, IApplication, TargetEnvironment, UrlServiceBase, ClientVersion, QueryParamService } from './shared/core/ovs.core';
import { ResortApplicationConfiguration } from './models/presentation.model';
//import { ApplicationParameters } from './models/browser.model';
import { ResortApplicationBase } from './services/resort-application.service';
import { environment } from '../environments/environment';
import { ApplicationConfigurationClient, ApplicationConfigurationClientBase } from './services/application-configuration.client';
import { ResortSummaryClient, ResortSummaryClientBase } from './services/resort-summary.client';
import { ResortDetailClient, ResortDetailClientBase } from './services/resort-detail.client';
import { routerTransition } from './shared/animations/router.animations';
import { CookieService } from '@ngx-toolkit/cookie';
import { StorageService } from './services/storage-service.client';
import { ResortEventData, ResortPageViews } from './instrumentation/resort-custom-events';
import { ResortInsightsService } from './instrumentation/resort-insights.service';
import { AppInsightsService } from 'microsoft-applicationinsights-angular5';
import { TealiumService } from './instrumentation/tealium/tealium.service';
import { BootstrapHelperService  } from './instrumentation/bootstrap-helper.service';
import { ImageService } from './services/image.service';

@Component({
    selector: 'app',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    animations: [ routerTransition ],
})
export class AppComponent extends OvsComponent implements IApplication
{
    private _configClient: ApplicationConfigurationClientBase;
    private _resortApplication: ResortApplicationBase;
    private _core: Core;
    private _googleKey: string;
    private _http: HttpClient;
    private _urlService: UrlServiceBase;
    private _location: Location;
    private _qryParamService: QueryParamService;
    favIcon: HTMLLinkElement = document.querySelector('#appIcon') as HTMLLinkElement;
    private vacationRewardsPartnerId: string = '11';

    public isLoaded: boolean = false;
    public headerIdentifier: string = "default"; 
    public environment: TargetEnvironment;
    public targetEnvironment: typeof TargetEnvironment = TargetEnvironment;

    

    constructor(configClient: ApplicationConfigurationClientBase, private route: ActivatedRoute, private router: Router, injector: Injector, core: Core, resortApplication: ResortApplicationBase, http: HttpClient, urlService: UrlServiceBase, qryParamService: QueryParamService, location: Location
        , private cookieService: CookieService, private storageService: StorageService, private btsHelperService:BootstrapHelperService, private imageService: ImageService)
    {
        super();
        console.log('Resort UI -> AppComponent');
        this._configClient = configClient;
        this._resortApplication = resortApplication;
        this._core = core;
        this._http = http;
        this._urlService = urlService;
        this._location = location;
        this._qryParamService = qryParamService;
        this.subscribe(core.isLoaded, v => this.isLoaded = v);
        Core.Run(this, injector);
    }

    protected initialize(): void
    {
        var partnerId = this._qryParamService.partnerId();
        var cobrandId = this._qryParamService.cobrandId();
        var appParams = this._qryParamService.applicationParams;

        if(partnerId && cobrandId && appParams.partnerId && appParams.cobrandId) {
            // we need to reload site to reload components with styles and member info.
            window.location.reload();
        }
    }

    public configure(params:any, completionCallback: (configuration: ApplicationConfiguration | Error) => void): void
    {
        this._configClient.GetClientVersion((value:ClientVersion):void => {
            this.cookieService.setItem("clientVersion",value.Version );
       });

        this._http.get<any>('config.json', { observe: 'response' })
            .subscribe((value: HttpResponse<any>) => 
            {
                if (value.ok)
                {
                    // Configure services whose URLs are not the root URL of this application.
                    // EXAMPLE:  this._urlService.register(MyNonRootUrlService, "https://someurl");
                    
                    try
                    {
                        this._configClient.Get(params, v => this.onGetConfigurationCompleted(v, completionCallback));
                    }
                    catch (ex)
                    {
                        completionCallback(ex);
                    }
                }
            }, e => completionCallback(e));
    }

    private onGetConfigurationCompleted(configuration: ResortApplicationConfiguration, completionCallback: (configuration: ApplicationConfiguration | Error) => void): void
    {
        if(configuration && !configuration.IsSessionAlive && configuration.ErrorRedirectUrl){
            window.location.href = configuration.ErrorRedirectUrl;
        }

        this.environment = configuration.Environment;
        this._googleKey = configuration.GoogleKey;
        this.imageService.initialize(configuration.ImageRootUrls);
        if(configuration.HasErrors){
            if(configuration.ErrorMessage){
                alert(configuration.ErrorMessage);
            }
            if(configuration.ErrorRedirectUrl) window.location.href = configuration.ErrorRedirectUrl;
        }
        if(configuration.IsClientSideTempDataPersistanceEnabled){
            this._qryParamService.store();
        }
        else{
            this._qryParamService.clear();
        }
        completionCallback(configuration);
    }

    public load(completionCallback: (error?: any) => void): void
    {
        
        if(!this.btsHelperService.isGoogleTagManagerLoaded){
            let script  = document.createElement('script');
            script.src = "https://maps.googleapis.com/maps/api/js?key=" + this._googleKey + "&libraries=places";
            document.body.insertBefore(script, document.body.firstChild);
            this.btsHelperService.isGoogleTagManagerLoaded = true;
        }
        let config: ResortApplicationConfiguration = <ResortApplicationConfiguration>this._core.currentConfiguration;

        if (this._core.currentConfiguration.Partner.Id == this.vacationRewardsPartnerId) {
            this.favIcon.href = "../../../assets/favicon/vacation-rewards.ico";
        }


        if (!String.isNullOrWhiteSpace(config.Stylesheet))
        {
            let styleTag = document.createElement('link');
            styleTag.rel = 'stylesheet';
            styleTag.type = 'text/css';
            styleTag.id = 'ovs-stylesheet';
            styleTag.href = config.Stylesheet;
            document.body.insertBefore(styleTag, document.body.firstChild);
        }

        if(config.ErrorRedirectUrl){
            this.cookieService.setItem("ErrorRedirectUrl", config.ErrorRedirectUrl, {
                expires: 180*60
            });
        }

        document.title = this._core.currentConfiguration.Partner.LongName;
        if(config.HeaderDisplayOptions.HeaderIdentifier) this.headerIdentifier= config.HeaderDisplayOptions.HeaderIdentifier;
        
		if (config.HasGoogleTagManager && config.GoogleTagManagerContainerId && !config.IsAgent) //test if containerId is provided
        {
            let tagMgrHeadScript  = document.createElement('script');
            let inlineScript = document.createTextNode("(function (w, d, s, l, i) { w[l] = w[l] || []; w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' }); var f = d.getElementsByTagName(s)[0], j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : ''; j.async = true; j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl; f.parentNode.insertBefore(j, f); })(window, document, 'script', 'dataLayer', '" + config.GoogleTagManagerContainerId + "');");
            tagMgrHeadScript.appendChild(inlineScript);
            document.head.appendChild(tagMgrHeadScript);

            let tagMgrScript  = document.createElement('noscript');
            tagMgrScript.innerHTML = "<iframe src=\"https://www.googletagmanager.com/ns.html?id=" + config.GoogleTagManagerContainerId + "\" height=\"0\" width=\"0\" style=\"display:none; visibility:hidden\"></iframe>";
            document.body.insertBefore(tagMgrScript, document.body.firstChild);
        }

        if (config.ShowOptimizely) {
            let script = document.createElement('script');
            script.src = "https://cdn.optimizely.com/public/12894770201/s/globalSnippet.js";
            document.head.insertBefore(script, document.head.firstChild);
        }

        let currentPath = this.route.snapshot.root.pathFromRoot.first().firstChild.url[0].path;
        this.router.navigate([currentPath!="resort-listing" && currentPath !="confirmation" ? currentPath: "search-resorts"], { queryParams: { sid: this._qryParamService.encodedGuid } });
        try
        {
            let ggl = eval("google");
        }
        catch (err)
        {
            setTimeout(() => completionCallback(), 1000);
            return;
        }
    }

    public error(error: any): void
    {
        let message: string = error;
        
        if (error.message)
            message = error.message;

        if (error.error && error.error.message)
            message = error.error.message;

        window.alert(message);
    }

    // for animation
    public getState(outlet) {
        return outlet.activatedRouteData.state;
    }

    @HostListener('window:beforeunload', ['$event'])
    onWindowClose(event) {
       // this._qryParamService.clear();
    }
}



// WEBPACK FOOTER //
// /home/vsts/work/1/s/Ovs.Resort.UI.Web/src/app/app.component.ts